'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, CheckCircle, ArrowRight, Download } from 'lucide-react';
import { ContactModal } from '@/components/contact-modal';
import { Breadcrumb } from '@/components/breadcrumb';

const standardTypes = [
  {
    title: '国家标准',
    description: '参与国家级标准的制定工作，提升企业在全国范围内的行业地位和影响力。',
    features: ['国家标准制定', '技术标准编写', '标准修订参与'],
  },
  {
    title: '行业标准',
    description: '参与行业标准制定，成为行业规则的制定者和引领者。',
    features: ['行业标准起草', '技术规范制定', '行业规则建立'],
  },
  {
    title: '团体标准',
    description: '发起或参与团体标准制定，快速响应市场需求，引领行业发展。',
    features: ['团体标准发起', '标准组织实施', '标准推广服务'],
  },
];

const process = [
  { step: 1, title: '需求对接', description: '了解企业标准参编需求和目标' },
  { step: 2, title: '方案制定', description: '制定标准参编方案和计划' },
  { step: 3, title: '组织申报', description: '协助企业完成标准立项申报' },
  { step: 4, title: '标准编写', description: '组织专家团队参与标准编写' },
  { step: 5, title: '审核发布', description: '跟进标准审核流程直至发布' },
];

const documents = [
  { name: '标准参编申请表', type: 'PDF', size: '256KB' },
  { name: '标准参编服务手册', type: 'PDF', size: '1.2MB' },
  { name: '成功案例集', type: 'PDF', size: '3.5MB' },
];

export default function StandardPage() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '标准参编' },
  ];

  return (
    <div className="flex flex-col">
      <Breadcrumb items={breadcrumbItems} />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="h-12 w-12 rounded-lg bg-blue-500/30 flex items-center justify-center mb-6">
              <FileText className="h-6 w-6" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight lg:text-5xl">标准参编服务</h1>
            <p className="mt-6 text-lg text-blue-100">
              协助企业参与国家标准、行业标准、团体标准的制定工作，
              提升企业在行业内的话语权和影响力，助力企业成为行业标准的制定者。
            </p>
            <Button
              size="lg"
              variant="default"
              className="mt-8 bg-white text-blue-900 hover:bg-gray-100"
              onClick={() => setIsContactOpen(true)}
            >
              立即咨询
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Standard Types */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">标准参编类型</h2>
            <p className="mt-4 text-lg text-gray-600">涵盖国家标准、行业标准、团体标准多个层次</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {standardTypes.map((type) => (
              <Card key={type.title} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>{type.title}</CardTitle>
                  <CardDescription className="text-gray-600">{type.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {type.features.map((feature) => (
                      <li key={feature} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-blue-600 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">参编流程</h2>
            <p className="mt-4 text-lg text-gray-600">标准化服务流程，确保项目高效推进</p>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {process.map((item) => (
              <div key={item.step} className="text-center">
                <div className="h-12 w-12 rounded-full bg-blue-600 text-white flex items-center justify-center mx-auto mb-4 font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">相关文件下载</h2>
            <p className="mt-4 text-lg text-gray-600">下载标准参编相关文件和资料</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {documents.map((doc) => (
              <Card key={doc.name} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="flex items-center p-6">
                  <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center mr-4">
                    <FileText className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{doc.name}</h3>
                    <p className="text-sm text-gray-500">{doc.type} • {doc.size}</p>
                  </div>
                  <Button size="sm" variant="ghost">
                    <Download className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold">开启标准参编之旅</h2>
          <p className="mt-4 text-lg text-blue-100 max-w-2xl mx-auto">
            立即联系我们，获取专业的标准参编服务方案
          </p>
          <div className="mt-8">
            <Button
              size="lg"
              variant="default"
              className="bg-white text-blue-600 hover:bg-gray-100"
              onClick={() => setIsContactOpen(true)}
            >
              免费咨询
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
}